import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AttemptListResponse, AttemptView, ExamListResponse, SaveAnswersInput } from '@jupi/shared'
import { api } from '@jupi/ui'

// The member app's exam calls (/api/exams).

export const examKeys = {
  all: ['exams'] as const,
  list: ['exams', 'list'] as const,
  mine: ['exams', 'attempts'] as const,
  attempt: (id: string) => ['exams', 'attempt', id] as const,
}

export function useExams() {
  return useQuery({ queryKey: examKeys.list, queryFn: () => api.get<ExamListResponse>('/api/exams') })
}

export function useMyAttempts() {
  return useQuery({ queryKey: examKeys.mine, queryFn: () => api.get<AttemptListResponse>('/api/exams/attempts') })
}

export function useAttempt(attemptId: string | undefined) {
  return useQuery({
    queryKey: examKeys.attempt(attemptId ?? ''),
    queryFn: () => api.get<AttemptView>(`/api/exams/attempts/${attemptId}`),
    enabled: Boolean(attemptId),
    // The clock and the answers are the server's; the page keeps its own
    // working copy of the answers and does not want them overwritten by a
    // refetch on focus.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  })
}

export function useStartAttempt() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ examId, locale }: { examId: string; locale?: string }) =>
      api.post<AttemptView>(`/api/exams/${examId}/attempts`, locale ? { locale } : {}),
    onSuccess: (view) => {
      queryClient.setQueryData(examKeys.attempt(view.attempt.attemptId), view)
      void queryClient.invalidateQueries({ queryKey: examKeys.list })
    },
  })
}

export function useSaveAnswers(attemptId: string) {
  return useMutation({
    mutationFn: (input: SaveAnswersInput) => api.put<void>(`/api/exams/attempts/${attemptId}/answers`, input),
  })
}

export function useSubmitAttempt(attemptId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveAnswersInput) => api.post<AttemptView>(`/api/exams/attempts/${attemptId}/submit`, input),
    onSuccess: (view) => {
      queryClient.setQueryData(examKeys.attempt(attemptId), view)
      void queryClient.invalidateQueries({ queryKey: examKeys.all })
    },
  })
}
